import { Component, OnInit, AfterViewInit, Inject, PLATFORM_ID, signal, effect, computed } from '@angular/core';
import { RouterOutlet, RouterLink, Router, NavigationEnd } from '@angular/router';
import { CommonModule, isPlatformBrowser, DOCUMENT } from '@angular/common';
import { Meta } from '@angular/platform-browser';

import { ChatWidgetComponent } from './components/chat-widget/chat-widget.component';
import { CiiBadgeComponent } from './components/cii-badge/cii-badge.component';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [RouterOutlet, RouterLink, CommonModule, ChatWidgetComponent, CiiBadgeComponent],
  templateUrl: './app.component.html',
  styleUrl: './app.component.css'
})
export class AppComponent implements OnInit, AfterViewInit {
  title = 'mesh-box';
  theme: ReturnType<typeof signal<'light' | 'dark' | 'auto'>>;
  isMenuOpen = signal(false);
  isBrowser: boolean;

  constructor(
    @Inject(PLATFORM_ID) platformId: Object,
    @Inject(DOCUMENT) private doc: Document,
    private router: Router,
    private meta: Meta
  ) {
    this.isBrowser = isPlatformBrowser(platformId);

    // Initialize theme with stored value to avoid double render
    const storedTheme = this.isBrowser
      ? (localStorage.getItem('theme') as 'light' | 'dark' | 'auto') || 'auto'
      : 'auto';
    this.theme = signal<'light' | 'dark' | 'auto'>(storedTheme);

    // Effect to apply theme changes to the DOM
    effect(() => {
      if (this.isBrowser) {
        const root = document.documentElement;
        const currentTheme = this.theme();

        if (currentTheme === 'dark') {
          root.setAttribute('data-theme', 'dark');
        } else if (currentTheme === 'light') {
          root.setAttribute('data-theme', 'light');
        } else {
          // Auto mode: check system preference
          const systemDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
          root.setAttribute('data-theme', systemDark ? 'dark' : 'light');
        }
      }
    });

    // Close menu and update logic on navigation
    this.router.events.subscribe(event => {
      if (event instanceof NavigationEnd) {
        this.isMenuOpen.set(false);
        this.updateCanonicalUrl();
        // initReveal is now handled automatically by the MutationObserver
      }
    });
  }

  ngOnInit() {
    this.meta.addTags([
      { name: 'author', content: 'Mesh Box' },
      { property: 'og:site_name', content: 'Mesh Box Agency' },
      { property: 'og:type', content: 'website' },
      { property: 'og:image', content: 'https://mesh-box.fr/assets/mesh_box.png' },
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:image', content: 'https://mesh-box.fr/assets/mesh_box.png' }
    ]);
    this.updateCanonicalUrl();
  }

  updateCanonicalUrl() {
    let link: HTMLLinkElement | null = this.doc.querySelector('link[rel="canonical"]');
    const rawPath = this.router.url.split('?')[0];
    // Strip trailing slash on non-root paths; keep '/' for homepage
    const normalizedPath = rawPath !== '/' ? rawPath.replace(/\/$/, '') : '/';
    const url = 'https://www.mesh-box.fr' + normalizedPath;
    if (link) {
      link.setAttribute('href', url);
    } else {
      link = this.doc.createElement('link');
      link.setAttribute('rel', 'canonical');
      link.setAttribute('href', url);
      this.doc.head.appendChild(link);
    }
  }

  ngAfterViewInit() {
    if (this.isBrowser && !navigator.userAgent.includes('HeadlessChrome')) {
      // Small delay to ensure Angular has finished rendering the first route
      setTimeout(() => {
        this.initReveal();
        this.doc.body.classList.add('js-ready');
        this.setupRevealObserver();
      }, 50);
    }
  }

  setupRevealObserver() {
    // Observe DOM changes to automatically reveal new elements
    let timeout: any;
    const mutationObserver = new MutationObserver((mutations) => {
      // Check if any added nodes contain .reveal elements
      const hasRevealElements = mutations.some(mutation =>
        Array.from(mutation.addedNodes).some(node => {
          if (node.nodeType === Node.ELEMENT_NODE) {
            const element = node as Element;
            return element.classList?.contains('reveal') ||
                   element.querySelector?.('.reveal');
          }
          return false;
        })
      );

      if (hasRevealElements) {
        // Debounce to avoid calling initReveal too frequently
        clearTimeout(timeout);
        timeout = setTimeout(() => this.initReveal(), 100);
      }
    });

    mutationObserver.observe(this.doc.body, {
      childList: true,
      subtree: true
    });
  }

  toggleTheme() {
    if (!this.isBrowser) return;

    const currentTheme = this.theme();
    let nextTheme: 'light' | 'dark' | 'auto';

    if (currentTheme === 'auto') {
      nextTheme = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'light' : 'dark';
    } else {
      nextTheme = currentTheme === 'dark' ? 'light' : 'dark';
    }

    this.theme.set(nextTheme);
    localStorage.setItem('theme', nextTheme);
  }

  toggleMenu() {
    this.isMenuOpen.update(v => !v);
  }

  initReveal() {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('visible');
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.1 });

    // Only observe elements that don't already have the 'visible' class (SSR-safe)
    const revealElements = document.querySelectorAll('.reveal:not(.visible)');
    revealElements.forEach(el => observer.observe(el));
  }
}
